/**
 * Vendors List Page
 *
 * Main page component for displaying and managing vendors.
 */

import { renderVendorList, initVendorListHandlers } from '../../components/vendors/VendorList.js';
import { apiClient } from '../../services/api.js';
import { t } from '../../i18n/translations.js';
import { toast } from '../../services/toast.js';

/**
 * Page state
 */
let state = {
  vendors: [],
  filters: {},
  pagination: { page: 1, totalPages: 1, totalItems: 0 },
  loading: true,
  error: null,
  // Modal state
  showCreateModal: false,
  saving: false,
  createForm: {
    name: '',
    category: '',
    email: '',
    phone: '',
  },
};

/**
 * Update page state and re-render
 */
function setState(newState) {
  state = { ...state, ...newState };
  render();
}

/**
 * Fetch vendors from API
 */
async function fetchVendors(page = 1, filters = {}) {
  setState({ loading: true, error: null });

  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: '20',
    });

    // Add filters
    if (filters.category) {
      params.append('category', filters.category);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    console.log('[Vendors] Fetching from API...');
    const response = await apiClient.get(`/v1/vendors?${params}`);
    console.log('[Vendors] API response:', response);

    // Handle wrapped API response
    let data = response.data || response;
    if (data && data.data !== undefined) {
      data = data.data;
    }
    console.log('[Vendors] Unwrapped data:', data);
    const items = Array.isArray(data) ? data : (data.data || data.items || []);
    console.log('[Vendors] Items:', items);
    const pageInfo = (typeof data === 'object' && !Array.isArray(data)) ? (data.pageInfo || {}) : {};

    setState({
      vendors: items,
      pagination: {
        page: pageInfo.page || page,
        totalPages: pageInfo.totalPages || 1,
        totalItems: pageInfo.totalItems || items.length || 0,
      },
      filters,
      loading: false,
    });
  } catch (err) {
    console.error('[Vendors] Failed to fetch vendors:', err);
    setState({
      vendors: [],
      pagination: {
        page: 1,
        totalPages: 1,
        totalItems: 0,
      },
      loading: false,
      error: err.message || 'Failed to load vendors',
    });
  }
}

/**
 * Initialize global handlers
 */
function initHandlers() {
  // Pagination handlers
  window.goToVendorPage = (page) => {
    fetchVendors(page, state.filters);
  };

  // Filter handlers
  window.applyVendorFilters = () => {
    const category = document.getElementById('filter-category')?.value || '';
    const search = document.getElementById('filter-search')?.value || '';

    const filters = {};
    if (category) filters.category = category;
    if (search) filters.search = search;

    fetchVendors(1, filters);
  };

  window.clearVendorFilters = () => {
    // Clear input fields
    const categoryEl = document.getElementById('filter-category');
    const searchEl = document.getElementById('filter-search');
    if (categoryEl) categoryEl.value = '';
    if (searchEl) searchEl.value = '';

    fetchVendors(1, {});
  };

  // Action handlers
  window.viewVendor = (id) => {
    window.location.hash = `vendors/${id}`;
  };

  window.editVendor = (id) => {
    window.location.hash = `vendors/${id}?edit=true`;
  };

  window.viewVendorContracts = (id) => {
    window.location.hash = `contracts?vendorId=${id}`;
  };

  window.viewVendorInvoices = (id) => {
    window.location.hash = `invoices?vendorId=${id}`;
  };

  window.createVendor = () => {
    setState({
      showCreateModal: true,
      createForm: { name: '', category: '', email: '', phone: '' }
    });
  };

  window.closeCreateVendorModal = () => {
    setState({ showCreateModal: false });
  };

  window.submitCreateVendor = async (event) => {
    event.preventDefault();

    const name = document.getElementById('vendor-name')?.value?.trim();
    const category = document.getElementById('vendor-category')?.value;
    const email = document.getElementById('vendor-email')?.value?.trim();
    const phone = document.getElementById('vendor-phone')?.value?.trim();

    if (!name) {
      toast.error(t('vendors.nameRequired') || 'Vendor name is required');
      return;
    }

    setState({ saving: true });

    try {
      const vendorData = {
        name,
        category: category || undefined,
      };

      // Only add contactInfo if there's data
      if (email || phone) {
        vendorData.contactInfo = {};
        if (email) vendorData.contactInfo.email = email;
        if (phone) vendorData.contactInfo.phone = phone;
      }

      await apiClient.post('/v1/vendors', vendorData);
      toast.success(t('vendors.createSuccess') || 'Vendor created successfully');
      setState({ showCreateModal: false, saving: false });
      await fetchVendors(1, state.filters);
    } catch (err) {
      console.error('Failed to create vendor:', err);
      toast.error(err.message || t('vendors.createFailed') || 'Failed to create vendor');
      setState({ saving: false });
    }
  };

  // Toggle action menu visibility
  window.toggleVendorActions = (event, id) => {
    event.stopPropagation();

    // Close all other open menus
    document.querySelectorAll('.action-menu.open').forEach(menu => {
      if (menu.dataset.vendorId !== id) {
        menu.classList.remove('open');
      }
    });

    // Toggle the clicked menu
    const menu = document.querySelector(`.action-menu[data-vendor-id="${id}"]`);
    if (menu) {
      const isOpen = menu.classList.contains('open');

      if (!isOpen) {
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();

        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.left = 'auto';
        menu.style.right = `${window.innerWidth - rect.right}px`;

        menu.classList.add('open');
        const menuRect = menu.getBoundingClientRect();
        if (menuRect.bottom > window.innerHeight) {
          menu.style.top = `${rect.top - menuRect.height - 4}px`;
        }
      } else {
        menu.classList.remove('open');
      }
    }
  };

  // Close action menus when clicking outside
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.action-menu-wrapper')) {
      document.querySelectorAll('.action-menu.open').forEach(menu => {
        menu.classList.remove('open');
      });
    }
  });

  // Delete vendor
  window.deleteVendor = async (id) => {
    // Close the action menu
    document.querySelectorAll('.action-menu.open').forEach(menu => {
      menu.classList.remove('open');
    });

    if (!confirm(t('vendors.deleteConfirm') || 'Are you sure you want to delete this vendor? This will not delete associated contracts or invoices.')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/vendors/${id}`);
      toast.success(t('vendors.deleteSuccess') || 'Vendor deleted successfully');

      // Refresh the vendors list
      await fetchVendors(state.pagination.page, state.filters);
    } catch (err) {
      console.error('Failed to delete vendor:', err);
      toast.error(t('vendors.deleteFailed') || 'Failed to delete vendor. Please try again.');
    }
  };

  // Initialize component handlers
  initVendorListHandlers();
}

/**
 * Render the create vendor modal
 */
function renderCreateVendorModal() {
  if (!state.showCreateModal) return '';

  return `
    <div class="modal-overlay" onclick="if(event.target === this) window.closeCreateVendorModal()">
      <div class="modal">
        <div class="modal-header">
          <h2>${t('vendors.addVendor') || 'Add Vendor'}</h2>
          <button class="modal-close" onclick="window.closeCreateVendorModal()">&times;</button>
        </div>
        <form class="modal-body" onsubmit="window.submitCreateVendor(event)">
          <div class="form-group">
            <label class="form-label">${t('vendors.name') || 'Name'} *</label>
            <input
              type="text"
              id="vendor-name"
              class="form-input"
              placeholder="${t('vendors.namePlaceholder') || 'Enter vendor name'}"
              required
            />
          </div>
          <div class="form-group">
            <label class="form-label">${t('vendors.category') || 'Category'}</label>
            <select id="vendor-category" class="form-select">
              <option value="">${t('common.selectOption') || 'Select...'}</option>
              <option value="technology">${t('vendors.categories.technology') || 'Technology'}</option>
              <option value="services">${t('vendors.categories.services') || 'Services'}</option>
              <option value="supplies">${t('vendors.categories.supplies') || 'Supplies'}</option>
              <option value="consulting">${t('vendors.categories.consulting') || 'Consulting'}</option>
              <option value="other">${t('vendors.categories.other') || 'Other'}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">${t('vendors.email') || 'Email'}</label>
            <input
              type="email"
              id="vendor-email"
              class="form-input"
              placeholder="${t('vendors.emailPlaceholder') || 'vendor@example.com'}"
            />
          </div>
          <div class="form-group">
            <label class="form-label">${t('vendors.phone') || 'Phone'}</label>
            <input
              type="tel"
              id="vendor-phone"
              class="form-input"
              placeholder="${t('vendors.phonePlaceholder') || '+1 (555) 123-4567'}"
            />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.closeCreateVendorModal()">
              ${t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" class="btn btn-primary" ${state.saving ? 'disabled' : ''}>
              ${state.saving ? `
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon btn-spinner">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                ${t('common.saving') || 'Saving...'}
              ` : (t('common.create') || 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

/**
 * Render the page
 */
function render() {
  const container = document.getElementById('vendors-page') || document.getElementById('app-content');
  if (!container) return;

  if (state.loading) {
    container.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="page vendors-page">
      ${renderVendorList({
        vendors: state.vendors,
        filters: state.filters,
        pagination: state.pagination,
        showFilters: true,
      })}
    </div>
    ${renderCreateVendorModal()}
  `;
}

/**
 * Page component export
 */
export default {
  /**
   * Initialize the page
   */
  async init() {
    initHandlers();
    await fetchVendors();
  },

  /**
   * Render the page content
   */
  render() {
    return `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
  },

  /**
   * Called after page is mounted
   */
  async mounted() {
    await this.init();
  },

  /**
   * Cleanup on page leave
   */
  destroy() {
    delete window.goToVendorPage;
    delete window.applyVendorFilters;
    delete window.clearVendorFilters;
    delete window.viewVendor;
    delete window.editVendor;
    delete window.viewVendorContracts;
    delete window.viewVendorInvoices;
    delete window.createVendor;
    delete window.closeCreateVendorModal;
    delete window.submitCreateVendor;
    delete window.deleteVendor;
  },
};
